<template>
    <div @blur="hideMenu"
         tabindex="0"
         class="border rounded mb-3 w-100 quill-editor"
         :class="{ 'highlighted-param': showMenu }">
        <quill-editor
            v-if="disabled"
            ref="editor"
            v-model="mapValueText"
            :options="quillOptions"
            disabled>
        </quill-editor>
        <quill-editor
            v-else
            ref="editor"
            v-model="mapValueText"
            :options="quillOptions"
            @copy.native="onQuillCopy"
            @paste.native="onQuillPaste"
            @focus="onQuillFocus"
            @change="onQuillChange($event)">
        </quill-editor>

        <portal to="APP_PARAM_MAP_MENU">
            <AppModuleMappableParameterMenu
                v-if="block && showMenu"
                :id="`map-param-menu-${id}`"
                :key="`map-param-menu-${id}`"
                @close="hideMenu"
                @select="onMenuItemSelect"
                @start-expression="startExpression"
                :block="block"
                :objectRefOnly="objectRefOnly"
                :disabled="disabled"
                :withComplexExpressionBtn="!isEvaluationExpression"
                class="map-param-menu"/>
        </portal>
    </div>
</template>

<script>
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import Quill from 'quill'
import {quillEditor} from 'vue-quill-editor'
import AppModuleMappableParameterMenu
    from '../../../../views/workflow/mappable-params/menu/AppModuleMappableParameterMenu'
import {
    APP_MAP_PARAM_FUNCTION,
    APP_MAP_PARAM_OBJECT,
    APP_MAP_PARAM_OPERATOR
} from '../../../../views/workflow/mappable-params/menu/app-module-mappable-param-menu'
import {mapGetters} from 'vuex'
import {getFromBetween, getRandomId, splitMulti} from '../../../../utils'
import {MAP_PARAM_COMPLEX_EXPR, MAP_PARAM_FUNCTION, MAP_PARAM_OBJECT_TAG, MAP_PARAM_OPERATOR} from '../index'
import {MapParamOperator} from './MapParamOperator'
import {MapParamFunction} from './MapParamFunction'
import {MapParamObjectTag} from './MapParamObjectTag'
import {MapParamComplexExpression} from './MapParamComplexExpression'
import {workflowOperators} from '../../../../models/workflow/api/operators'
import {workflowFunctions} from '../../../../models/workflow/api/functions'
import {convertAppParamQuillHtmlStringToApiString} from './index'

const Parchment = Quill.imports.parchment
const Delta = Quill.imports.delta

export default {
    name: 'AppMapParamQuillEditor',
    components: {quillEditor, AppModuleMappableParameterMenu},
    props: {
        id: {
            type: String,
            required: false,
            default: getRandomId.bind(null, 'map-param')
        },
        value: {},
        block: {
            // type: Object,
            required: true
        },
        objectRefOnly: { // for datastore operation => disable typing + remove {{ }}
            type: Boolean,
            required: false,
            default: false
        },
        isEvaluationExpression: { // means that the whole value will be wrapped with {{ }} (without any other parenthesis between)
            type: Boolean,
            required: false,
            default: false
        },
        disabled: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    data() {
        return {
            showMenu: false,

            mapValue: '', // real value for the server (objects formatted like "{{ objectId.prop1name.prop2name.prop3name... }}" )
            mapValueText: '', // displayed in quill (objects formatted like "@ObjectLabel.Prop1Label.Prop2Label.Prop3Label" )

            isLoading: false,

            cursor: 0,
            quillOptions: {
                placeholder: 'Enter expression',
                modules: {
                    toolbar: false
                }
            }
        }
    },
    computed: {
        ...mapGetters([
            'getWorkflowObjectById',
            'getWorkflowObjectLabelWithBlockNamePrefix'
        ]),

        quill() {
            return this.$refs.editor.quill
        },

        expressionOperators() {
            return Object.values(workflowOperators).map(o => o.list).flat(1)
        },

        expressionFunctions() {
            return Object.values(workflowFunctions).map(o => o.list).flat(1)
        }

    },
    methods: {
        openMenu() {
            if (!this.isLoading) {
                this.$nextTick(() => {
                    this.showMenu = true
                })
            }
        },

        hideMenu() {
            this.showMenu = false
        },

        onMenuItemSelect({type, data}) {
            if (!this.disabled) {
                if (type === APP_MAP_PARAM_OBJECT) {
                    this.insertObjectPropAccess(data)
                } else if (type === APP_MAP_PARAM_OPERATOR) {
                    this.insertOperator(data)
                } else if (type === APP_MAP_PARAM_FUNCTION) {
                    this.insertFunction(data)
                }
                this.focusQuill()
            }
        },

        async insertObjectPropAccess({workflowObject, fieldPath}) {
            if (!workflowObject || !_.isArray(workflowObject.data) || !_.isArray(fieldPath)) { // object can be deleted by user
                return
            }
            workflowObject = _.cloneDeep(workflowObject)
            workflowObject.label = this.getWorkflowObjectLabelWithBlockNamePrefix(workflowObject)
            const fieldsChain = this.getWorkflowObjectFieldsChainByPath(workflowObject.data, fieldPath)
            if (this.objectRefOnly) {
                this.quill.setContents(
                    new Delta()
                        .insert({[MAP_PARAM_OBJECT_TAG]: {workflowObject, fieldsChain}})
                )
            } else {
                this.quill.updateContents(
                    new Delta()
                        .retain(this.cursor)
                        .insert({[MAP_PARAM_OBJECT_TAG]: {workflowObject, fieldsChain}})
                )
            }
            this.cursor += 1
            this.quill.setSelection(this.cursor, 0) // contenteditable=false treated text like length 1
        },

        insertOperator(operator) {
            this.quill.updateContents(
                new Delta()
                    .retain(this.cursor)
                    .insert({[MAP_PARAM_OPERATOR]: {operator}})
            )
            this.cursor += 1
            this.quill.setSelection(this.cursor, 0)
        },

        insertFunction(fun) {
            this.quill.updateContents(
                new Delta()
                    .retain(this.cursor)
                    .insert({[MAP_PARAM_FUNCTION]: {fun}})
            )
            this.cursor += 1
            this.quill.setSelection(this.cursor, 0)
        },

        insertTextWithAutoHighlight(text = '') {
            if (this.expressionOperators.includes(text.trim())) {
                this.insertOperator(text)
            } else if (this.expressionFunctions.includes(text.trim())) {
                this.insertFunction(text)
            } else {
                if (typeof text === 'string' && text.trim()) {
                    text = ` ${text} `
                }
                this.quill.insertText(this.cursor, text)
                this.cursor += text.length
                this.quill.setSelection(this.cursor, 0)
            }
        },

        focusQuill() {
            this.quill.setSelection(this.cursor, 0)
        },

        onQuillFocus() {
            this.openMenu()
        },

        onQuillCopy(e) {
            e.clipboardData.setData('text/plain', this.value)
            e.preventDefault()
        },

        async onQuillPaste(e) {
            e.stopPropagation()
            e.preventDefault()
            let text = (e.originalEvent || e).clipboardData.getData('text/plain')
            if (typeof text !== 'string') {
                return
            }
            this.isLoading = true
            text = text.replace(/[\n\r\s]+/g, ' ')
            if (text.includes('{{')) { // we can do only full replace value copied from another mappable field
                this.quill.setText('')
                await this.$nextTick()
            }
            this.convertMapValueToQuillString(text, this.objectRefOnly)
            this.isLoading = false

            // manually run quill change in order to update map value for the api
            await this.$nextTick()
            this.onQuillChange({
                html: this.$refs.editor.value,
                text: this.quill.getText()
            })
        },

        onQuillChange({html, text}) {
            if (this.isLoading) {
                return
            }
            if (this.objectRefOnly) {
                if (text && text.length > 1) { // typing
                    this.quill.setContents([])
                }
            }
            if (this.isEvaluationExpression) {
                this.mapValue = `{{ ${convertAppParamQuillHtmlStringToApiString(html, false)} }}`
            } else {
                this.mapValue = convertAppParamQuillHtmlStringToApiString(html)
            }

            const range = this.quill.getSelection()
            if (range) {
                this.cursor = range.index
            }
        },

        convertMapValueToQuillString(value = '', objectRefOnly = false) {
            try {
                if (!value || !_.isString(value)) {
                    return
                }
                let workflowObject, expression, objTagParts, fieldPath
                let isComplexExpressionLastInsert = false

                if (objectRefOnly) {
                    const objTagParts = value.split('.').map(el => el.toString().trim())
                    if (objTagParts[0]) { // objectId
                        workflowObject = this.getWorkflowObjectById(objTagParts[0])
                        fieldPath = objTagParts.slice(1)
                        this.insertObjectPropAccess({workflowObject, fieldPath})
                    }
                } else {
                    const expressions = getFromBetween.get(value, '{{', '}}')
                    if (expressions.length > 0) {
                        splitMulti(value, expressions).forEach((text, idx) => {
                            if (isComplexExpressionLastInsert) {
                                this.quill.insertText(this.quill.getLength() - 1, ' ')
                                this.cursor += 1
                                this.quill.setSelection(this.cursor, 0)
                            }

                            text = text.replace('{{', '').replace('}}', '')
                            this.insertTextWithAutoHighlight(text)

                            expression = expressions[idx]
                            if (expression) {
                                const exprParts = splitMulti(expression, [' '], false).filter(v => typeof v === 'string' && !!v.trim())
                                if (!this.isEvaluationExpression && exprParts.length > 1) {
                                    this.startExpression()
                                }
                                exprParts.forEach(exprPart => {
                                    if (exprPart.trim().startsWith('data_')) {
                                        objTagParts = exprPart.split('.').map(el => el.toString().trim())
                                        if (objTagParts[0]) { // objectId
                                            workflowObject = this.getWorkflowObjectById(objTagParts[0])
                                            fieldPath = objTagParts.slice(1)
                                            this.insertObjectPropAccess({workflowObject, fieldPath})
                                        }
                                    } else {
                                        this.insertTextWithAutoHighlight(exprPart)
                                    }
                                })

                                isComplexExpressionLastInsert = exprParts.length > 1
                            }
                        })
                    } else {
                        this.quill.insertText(this.cursor, value)
                        this.cursor += value.length
                    }
                }
            } catch (e) {
                this.$raven.captureException(e)
            }
        },

        getWorkflowObjectFieldsChainByPath(fields, fieldPath) {
            if (_.isEmpty(fieldPath)) {
                return []
            }
            let arr = []
            let field
            field = fields.find(f => f.name === fieldPath[0])
            if (field) {
                arr.push(field)
                if (field.spec) {
                    arr = arr.concat(this.getWorkflowObjectFieldsChainByPath(field.spec, fieldPath.slice(1)))
                }
            }
            return arr
        },

        startExpression() {
            this.quill.updateContents(
                new Delta()
                    .retain(this.cursor)
                    .insert({[MAP_PARAM_COMPLEX_EXPR]: {value: ' '}})
                    .insert(' ')
            )
            this.quill.setSelection(this.cursor, 0)
        }
    },
    mounted() {
        this.isLoading = true
        MapParamOperator.register()
        MapParamFunction.register()
        MapParamObjectTag.register()
        MapParamComplexExpression.register()
        this.convertMapValueToQuillString(this.value, this.objectRefOnly)

        this.quill.on('selection-change', (range, oldRange, source) => {
            if (range) {
                if (range.length === 0) {
                    this.cursor = range.index
                    // console.log('User cursor is on', range.index)
                } else {
                    // const text = quill.getText(range.index, range.length)
                    // console.log('User has highlighted', text);
                }
            } else {
                // console.log('Cursor not in the editor');
            }
        })
        this.quill.blur()
        this.isLoading = false
    },
    watch: {
        mapValue(value) {
            if (!this.isLoading) {
                this.$emit('change', value)
            }
        }
    }
}
</script>

<style lang="scss">
    .quill-editor {
        .map-param-object-tag {
            padding: 2px 0 2px 2px;
            border-radius: 4px;
            margin: 0 2px;
        }

        .map-param-complex-expr {
            border: 1px dashed $color-gold;
            line-height: 22px;
            padding: 2px 0;
            border-radius: 4px;
            min-width: 5px;

            &:blank::before {
                content: attr(data-placeholder);
                display: inline-block;
            }
        }
    }
</style>
